<script lang="ts" setup>
interface ui {
    wrapper?: string;
    base?: string;
    divide?: string;
    thead?: string;
    tbody?: string;
    caption?: string;
    tr?: {
        base?: string;
        selected?: string;
        active?: string;
    };
    th?: {
        base?: string;
        padding?: string;
        color?: string;
        font?: string;
        size?: string;
    };
    td?: {
        base?: string;
        padding?: string;
        color?: string;
        font?: string;
        size?: string;
    };
    checkbox?: {
        padding?: string;
    };
    loadingState?: {
        wrapper?: string;
        label?: string;
        icon?: string;
    };
    emptyState?: {
        wrapper?: string;
        label?: string;
        icon?: string;
    };
    progress?: {
        wrapper?: string;
    };
    default?: {
        sortAscIcon?: string;
        sortDescIcon?: string;
        sortButton?: {
            icon?: string;
            trailing: true;
            square: true;
            color?: string;
            variant?: string;
            class?: string;
        };
        checkbox?: {
            color?: string;
        };
        progress?: {
            color?: string;
            animation?: string;
        };
        loadingState?: {
            icon?: string;
            label?: string;
        };
        emptyState?: {
            icon?: string;
            label?: string;
        };
    };
}

export interface UiDataTablePropsBase {
    hideEmpty?: boolean;
    placement?:
        | "baseline"
        | "top"
        | "middle"
        | "bottom"
        | "text-top"
        | "text-bottom"
        | "sub"
        | "super";
    ui?: ui;
    labelClass?: string;
    valueClass?: string;
    labelTextClass?: string;
    valueTextClass?: string;
    transformLabel?: (label: string) => string;
}

export interface UiDataTableProps extends UiDataTablePropsBase {
    sub?: UiDataTablePropsBase;
}

const props = defineProps<UiDataTableProps>();
const items = defineModel<any>("items", { default: () => [] });

const innerPlacement = computed(() => {
    return props.placement
        ? {
              baseline: "align-baseline",
              top: "align-top",
              middle: "align-middle",
              bottom: "align-bottom",
              "text-top": "align-text-top",
              "text-bottom": "align-text-bottom",
              sub: "align-sub",
              super: "align-super",
          }[props.placement]
        : undefined;
});

const rows = computed(() => {
    let rows: {
        label: { class: string; value: string };
        value: { class: string; value: any };
    }[] = [];

    if (items.value && typeof items.value === "object") {
        rows = Object.entries(items.value).map(([label, value]) => ({
            label: {
                value: props.transformLabel
                    ? props.transformLabel(label)
                    : label,
                class: props.labelClass ?? "",
            },
            value: {
                value:
                    Array.isArray(value) && value.length === 1
                        ? value[0]
                        : value,
                class: props.valueClass ?? "",
            },
        }));
    }

    if (props.hideEmpty) {
        rows = rows.filter((item) => {
            return !(
                !item.value ||
                !item.value?.value ||
                (typeof item.value?.value === "string" &&
                    !item.value?.value.trim()) ||
                (typeof item.value?.value === "object" &&
                    !Object.entries(item.value.value).filter(([k, v]) => v)
                        .length)
            );
        });
    }

    if (innerPlacement.value) {
        rows = rows.map((item) => {
            item.label.class += ` ${innerPlacement.value}`;
            item.value.class += ` ${innerPlacement.value}`;
            return item;
        });
    }

    return rows;
});
</script>

<template>
    <UTable
        :rows="rows"
        :columns="[
            { label: 'Label', key: 'label' },
            { label: 'Value', key: 'value' },
        ]"
        :ui="{
            thead: 'hidden',
            divide: '',
            tbody: '',
            td: {
                base: '',
                ...(ui?.td ?? {}),
            },
            ...ui,
        }"
    >
        <template #label-data="{ row }">
            <span class="font-medium text-gray-900" :class="labelTextClass">{{
                row.label.value
            }}</span>
        </template>
        <template #value-data="{ row }">
            <UiDataTable
                v-if="typeof row.value.value === 'object'"
                :items="row.value.value"
                :hide-empty="sub?.hideEmpty ?? hideEmpty"
                :ui="sub?.ui ?? ui"
                :placement="sub?.placement ?? placement"
                :labelClass="sub?.labelClass ?? labelClass"
                :valueClass="sub?.valueClass ?? valueClass"
                :labelTextClass="sub?.labelTextClass ?? labelTextClass"
                :valueTextClass="sub?.valueTextClass ?? valueTextClass"
                :transform-label="sub?.transformLabel ?? transformLabel"
            />
            <ClientOnly v-else>
                <UiDataItem
                    :item-class="[valueTextClass, 'prose-sm']"
                    :model-value="row.value.value"
                />
            </ClientOnly>
            <!--            <span  v-else>{{ row.value.value }}</span>-->
        </template>
    </UTable>
</template>
